import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import getAssetUrl from '../lib/CDNAsset.js';

import { Water, Recycle, Clock, Percent, PatchQuestion, DropletFill, Rulers, LifePreserver } from 'react-bootstrap-icons';

import loadable from '@loadable/component';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';

import CentralLink from '../components/CentralLink.js'

import StaticMap from '../components/StaticMap.js'
import NonStaticMap from '../components/NonStaticMap.js'

import { getSearchSewageByPlaceId } from '../store/reducers/fmAPI.js';

import SpillCountExplainModal from '../components/SpillCountExplainModal.js';
import SewageDischargeStationOperationalHistoryChart from '../components/SewageDischargeStationOperationalHistoryChart.js';
import SewageDischargeStationSpillHistoryChart from '../components/SewageDischargeStationSpillHistoryChart.js';
import NearBySewagePlace from '../components/NearBySewagePlace.js';

import AdComponent from '../components/AdComponent.js';

import { sendNavigateToSearchSewageReport } from '../components/GoogleAnalytics.js';

import CommonHeaders from '../components/helmet-headers/CommonHeaders.js';
import Breadcrumbs, { CRUMBS } from '../components/helmet-headers/Breadcrumbs.js';

const SewageDischargeStationVolumeHistoryChart = loadable(() => import('../components/SewageDischargeStationVolumeHistoryChart.js'));

const MarkerLoadable = loadable(() => import('../components/leaflet-loadables/MarkerLoadable.js'), { fallback: null, ssr: false });
const Popup = loadable(() => import('react-leaflet').then(module => ({ default: module.Popup })), { fallback: null, ssr: false });

const SewagePlaceHistory = loadable(() => import('../components/sewage-place-renderers/SewagePlaceHistory.js'));
const SewagePlaceLive = loadable(() => import('../components/sewage-place-renderers/SewagePlaceLive.js'));

function SewageSearchPlace({ historical }) {
	const params = useParams();
	const dispatch = useDispatch();
	const [waitingOnApi, setWaitingOnApi] = useState(false);
	const [apiDidError, setApiDidError] = useState(false);
	const { searchSewageResults } = useSelector(state => state.fmAPI);

	const hasMatch = searchSewageResults && searchSewageResults[params.placeId];

	const formatNumberOneDP = function(hours) {
		return Math.floor(hours*10)/10;
	}

	const fetchApiData = function() {
		setWaitingOnApi(true); setApiDidError(false);
		dispatch(getSearchSewageByPlaceId(params.placeId, 'localSearchPlace.js', function(error) {
			setApiDidError(!!error);
			setWaitingOnApi(false);
		}));
	};
	useEffect(fetchApiData, [dispatch, params.placeId]);

	

	if(waitingOnApi && (!searchSewageResults || !searchSewageResults[params.placeId])) {
		return <>Loading...</>;
	}
	if(!hasMatch) {
		return <>404. Not Found</>;
	}

	const place = searchSewageResults[params.placeId];

	const hasLiveSewage = place && place.liveSewage && Array.isArray(place.liveSewage) && place.liveSewage.length > 0;
	let renderMode = historical ? 'historical' : (hasLiveSewage ? 'live' : 'historical');

	return (
		<div>
			<CommonHeaders
				canonicalUrlPath={"/data-explorer/search-sewage-report/" + place.placeId + "/"+place.placeName+(historical && hasLiveSewage ? "/historical": "")}
				canonicalDefaultWebsite="UK"
				ogType={"place"}
				lat={place.lat}
				lng={place.lng}
				townName={place.placeName}
				region={place.regionName}
				country={"United Kingdom"}
			/>
			<Breadcrumbs
				builtinCrumbs={[CRUMBS.HOME, CRUMBS.DATA_EXPLORER, CRUMBS.SEARCH_SEWAGE]}
				leafCrumbs={[{
					name: place.placeName,
					path: '/data-explorer/search-sewage-report/'+place.placeId + "/"+place.placeName
				}]}
			/>
			<Row className="align-items-center">
				<Col xs={12} className="text-white bg-dark"><h1 className="h1-blendin hX-scale15em"><b>{place.placeName}{place.placeCounty ? " (" + place.placeCounty + ")" : ""} - Sewage Summary</b></h1></Col>
			</Row>
			<Row className="screen-bottom-border">
				<Col xs={12} lg={1} xl={2} className="d-none d-lg-block large-screen-left">&nbsp;</Col>
				<Col xs={12} lg={10} xl={8}>
					<Row>
						<Col xs={12} style={{ paddingLeft: 0, paddingRight: 0 }}>
							<NonStaticMap
								lat={place.lat}
								lng={place.lng}
								label={place.placeName || "Local Area"}
								noDefaultMarker={true}
								isStatic={false}
								zoom={12}
								style={{height: "45vh", width: "100%"}}
							>
								{renderMode === 'live' && place && place.liveSewage && Array.isArray(place.liveSewage) ? place.liveSewage.map(function(metric) {
									console.log("MMMM", metric);
									return <MarkerLoadable
										key={metric.metricId}
										position={[metric.lat, metric.lng]}
										colour={metric.status === 'START' ? 'red' : (metric.status === 'OFFLINE' ? "orange" : "green_small")}
									>
										<Popup>
											<div style={{ textAlign: 'center', padding: '10px' }}>
											  <h3 className="hX-blendin header-bold hX-scale12em" style={{ margin: 0 }}>
											    {metric.stationName}
											  </h3>
											  {metric.riverName && (
											    <>
											      <p style={{ margin: '10px 0 5px 0' }}>
											        <strong>Discharging to:</strong> {metric.riverName}<br />
											      </p>
											      <hr style={{ margin: '10px 0' }} />
											    </>
											  )}
											 	<CentralLink to={"/stations/"+metric.stationId+"/metric/"+metric.metricId}>
												  <Button variant="dark" size="sm">
												    View Station
												  </Button>
												</CentralLink>
											</div>
										</Popup>
									</MarkerLoadable>;
								}) : null}
								{renderMode === 'historical' && place && place.stations ? Object.keys(place.stations).map(function(stationId) {
									let station = place.stations[stationId];
									return <MarkerLoadable
										key={stationId}
										position={[station.lat, station.lng]}
										colour="orange"
									>
									<Popup>
						              <div style={{ textAlign: 'center', padding: '10px' }}>
										  <h3 className="hX-blendin header-bold hX-scale12em" style={{ margin: 0 }}>
										    {station.stationName}
										  </h3>
										  {station.riverName && (
										    <>
										      <p style={{ margin: '10px 0 5px 0' }}>
										        <strong>Discharging to:</strong> {station.riverName}<br />
										      </p>
										      <hr style={{ margin: '10px 0' }} />
										    </>
										  )}
										 	<CentralLink to={"/stations/"+station.stationId}>
											  <Button variant="dark" size="sm">
											    View Station
											  </Button>
											</CentralLink>
										</div>

						            </Popup>
									</MarkerLoadable>
								}) : null}
							</NonStaticMap>
						</Col>
					</Row>
					{hasLiveSewage ? <Row>
						<Col xs={12} style={{borderBottom: "1px dashed black"}} className="pt-1 pb-1">
							<ButtonGroup>
								<CentralLink to={"/data-explorer/search-sewage-report/" + place.placeId + "/"+place.placeName}>
									<Button variant={historical ? "dark" : "secondary"} style={{marginLeft: "2px", marginRight: "2px"}} size="sm">Live Report</Button>
								</CentralLink>
								<CentralLink to={"/data-explorer/search-sewage-report/" + place.placeId + "/"+place.placeName + "/historical"}>
									<Button variant={!historical ? "dark" : "secondary"} style={{marginLeft: "2px", marginRight: "2px"}} size="sm">Historical</Button>
								</CentralLink>
							</ButtonGroup>
						</Col>
					</Row> : null}
					<Row>
						<Col xs={12}>
							{renderMode === 'live' ? <SewagePlaceLive place={place} /> : null}
							{renderMode === 'historical' ? <SewagePlaceHistory place={place} /> : null}
						</Col>
					</Row>
				</Col>
				<Col xs={12} lg={1} xl={2} className="d-none d-lg-block large-screen-right">&nbsp;</Col>
			</Row>
		</div>
	);
}

export default SewageSearchPlace;